import { resolve } from "path";
import { resolvePlatformCapacityPreset } from "@gencow/core";

type LoggerLike = Pick<Console, "log" | "warn" | "error">;

type ProvisionerSleepBridge = {
  canSleep(appName: string): boolean;
  sleepApp(appName: string): Promise<void>;
  getLastAccess(appName: string): number | undefined;
  getAppMeta(): ReadonlyMap<string, unknown>;
  getWakeCapacitySnapshot?(): unknown;
};

type AppStatusRow = {
  name: string;
  status: string;
  hasBackend: boolean;
};

type SleepMaintenanceConfig = {
  maxSleepPerCycle: number;
  sleepTimeoutMinutes: number;
  evictionThresholdMB: number | null;
};

type SleepCandidate = {
  appName: string;
  reason: "ghost_idle" | "idle_timeout";
  lastAccess: number;
};

const PLATFORM_MAINTENANCE_TOKEN_HEADER = "X-Platform-Maintenance-Token";

function parsePositiveInt(value: string | undefined): number | null {
  if (!value || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function readPlatformMaintenanceToken(env: Record<string, string | undefined>): string {
  return env.GENCOW_MAINTENANCE_TOKEN || env.PLATFORM_INTERNAL_SECRET || env.BETTER_AUTH_SECRET || "";
}

export function createPlatformMaintenanceTokenResolver(
  startupEnv: Record<string, string | undefined> = process.env,
): (env?: Record<string, string | undefined>) => string {
  const startupToken = readPlatformMaintenanceToken(startupEnv);
  return (env = startupEnv) => readPlatformMaintenanceToken(env) || (env === startupEnv ? startupToken : "");
}

const resolvePlatformMaintenanceToken = createPlatformMaintenanceTokenResolver(process.env);

export function loadSleepMaintenanceConfig(params: {
  env?: Record<string, string | undefined>;
  maxSleepPerCycle?: number;
  sleepTimeoutMinutes?: number;
}): SleepMaintenanceConfig {
  const env = params.env ?? process.env;
  const preset = resolvePlatformCapacityPreset(env);
  return {
    maxSleepPerCycle:
      params.maxSleepPerCycle ?? parsePositiveInt(env.GENCOW_SLEEP_MAX_PER_CYCLE) ?? preset.sleepMaxPerCycle,
    sleepTimeoutMinutes:
      params.sleepTimeoutMinutes ??
      parsePositiveInt(env.GENCOW_SLEEP_TIMEOUT_MINUTES) ??
      preset.sleepTimeoutMinutes,
    evictionThresholdMB: parsePositiveInt(env.EVICTION_THRESHOLD_MB) ?? preset.evictionThresholdMB,
  };
}

function readAvailableRamMB(snapshot: unknown): number {
  if (!snapshot || typeof snapshot !== "object") return Infinity;
  const availableRamMB = (snapshot as { availableRamMB?: unknown }).availableRamMB;
  return typeof availableRamMB === "number" ? availableRamMB : Infinity;
}

export function isMemoryPressure(availableRamMB: number, evictionThresholdMB: number | null): boolean {
  return evictionThresholdMB !== null && availableRamMB < evictionThresholdMB;
}

function buildSleepCandidates(params: {
  metaMap: ReadonlyMap<string, unknown>;
  dbStatusMap: Map<string, { status: string; hasBackend: boolean }>;
  getLastAccess(appName: string): number | undefined;
  canSleep(appName: string): boolean;
  memoryPressure: boolean;
}): SleepCandidate[] {
  const ghostCandidates: SleepCandidate[] = [];
  const idleCandidates: SleepCandidate[] = [];

  for (const [appName] of params.metaMap) {
    const dbInfo = params.dbStatusMap.get(appName);
    if (dbInfo && !dbInfo.hasBackend) continue;

    const lastAccess = params.getLastAccess(appName) || 0;
    if (dbInfo?.status === "idle") {
      ghostCandidates.push({ appName, reason: "ghost_idle", lastAccess });
      continue;
    }

    if (params.canSleep(appName)) {
      idleCandidates.push({ appName, reason: "idle_timeout", lastAccess });
    }
  }

  if (params.memoryPressure) {
    idleCandidates.sort((a, b) => a.lastAccess - b.lastAccess);
  }

  return [...ghostCandidates, ...idleCandidates];
}

export function registerUsageSnapshotCron(params: {
  platformPort: string;
  logger?: LoggerLike;
  fetchImpl?: typeof fetch;
  setIntervalImpl?: typeof setInterval;
  setTimeoutImpl?: typeof setTimeout;
  usageCronIntervalMs?: number;
  initialDelayMs?: number;
  env?: Record<string, string | undefined>;
  maintenanceToken?: string;
}): void {
  const logger = params.logger ?? console;
  const fetchImpl = params.fetchImpl ?? fetch;
  const setIntervalImpl = params.setIntervalImpl ?? setInterval;
  const setTimeoutImpl = params.setTimeoutImpl ?? setTimeout;
  const usageCronIntervalMs = params.usageCronIntervalMs ?? 60 * 60 * 1000;
  const initialDelayMs = params.initialDelayMs ?? 10_000;
  const snapshotUrl = `http://localhost:${params.platformPort}/internal/usage/snapshot`;
  const maintenanceToken =
    params.maintenanceToken ?? resolvePlatformMaintenanceToken(params.env ?? process.env);
  const snapshotRequest: RequestInit = maintenanceToken
    ? { method: "POST", headers: { [PLATFORM_MAINTENANCE_TOKEN_HEADER]: maintenanceToken } }
    : { method: "POST" };

  setIntervalImpl(async () => {
    try {
      const res = await fetchImpl(snapshotUrl, snapshotRequest);
      if (res.ok) {
        const result = (await res.json()) as Record<string, unknown>;
        logger.log(`[usage-cron] Storage snapshot ✓`, result);
      } else {
        logger.warn(`[usage-cron] Snapshot failed: ${res.status}`);
      }
    } catch (e: unknown) {
      logger.warn(`[usage-cron] Snapshot error:`, e instanceof Error ? e.message : String(e));
    }
  }, usageCronIntervalMs);

  setTimeoutImpl(async () => {
    try {
      await fetchImpl(snapshotUrl, snapshotRequest);
      logger.log(`[usage-cron] Initial snapshot ✓`);
    } catch {
      /* server not ready yet */
    }
  }, initialDelayMs);

  logger.log(`[platform] Usage cron registered (every 1h, first in 10s)`);
}

export function registerPlatformSleepChecker(params: {
  functionsPath: string;
  fetchAppStatuses: () => Promise<AppStatusRow[]>;
  logger?: LoggerLike;
  setIntervalImpl?: typeof setInterval;
  sleepCheckIntervalMs?: number;
  maxSleepPerCycle?: number;
  sleepTimeoutMinutes?: number;
  env?: Record<string, string | undefined>;
  importProvisioner?: (path: string) => Promise<ProvisionerSleepBridge>;
}): void {
  const logger = params.logger ?? console;
  const setIntervalImpl = params.setIntervalImpl ?? setInterval;
  const sleepCheckIntervalMs = params.sleepCheckIntervalMs ?? 5 * 60 * 1000;
  const config = loadSleepMaintenanceConfig({
    env: params.env,
    maxSleepPerCycle: params.maxSleepPerCycle,
    sleepTimeoutMinutes: params.sleepTimeoutMinutes,
  });
  const importProvisioner =
    params.importProvisioner ?? ((path: string) => import(path) as Promise<ProvisionerSleepBridge>);

  setIntervalImpl(async () => {
    let sleepCount = 0;

    try {
      const provisioner = await importProvisioner(resolve(params.functionsPath, "../src/provisioner.ts"));
      const metaMap = provisioner.getAppMeta();
      const availableRamMB = readAvailableRamMB(provisioner.getWakeCapacitySnapshot?.());
      const memoryPressure = isMemoryPressure(availableRamMB, config.evictionThresholdMB);
      const allApps = await params.fetchAppStatuses();
      const dbStatusMap = new Map<string, { status: string; hasBackend: boolean }>(
        allApps.map((app) => [app.name, { status: app.status, hasBackend: app.hasBackend }]),
      );

      const candidates = buildSleepCandidates({
        metaMap,
        dbStatusMap,
        getLastAccess: provisioner.getLastAccess,
        canSleep: provisioner.canSleep,
        memoryPressure,
      });

      for (const candidate of candidates.slice(0, config.maxSleepPerCycle)) {
        const idleMin = Math.floor((Date.now() - candidate.lastAccess) / 60_000);
        try {
          await provisioner.sleepApp(candidate.appName);
          if (candidate.reason === "ghost_idle") {
            logger.log(`[sleep] SWEEP: ${candidate.appName} is "idle" in DB but active in memory — slept ✓`);
          } else if (memoryPressure) {
            logger.log(
              `[sleep] ${candidate.appName}: memory pressure ${availableRamMB}/${config.evictionThresholdMB}MB, idle ${idleMin}m — evicting ✓`,
            );
          } else {
            logger.log(`[sleep] ${candidate.appName}: idle ${idleMin}m — sleeping ✓`);
          }
          sleepCount += 1;
        } catch (e: unknown) {
          logger.error(`[sleep] Failed to sleep ${candidate.appName}:`, e);
        }
      }

      if (sleepCount > 0) {
        logger.log(`[sleep] ${sleepCount} app(s) put to sleep this cycle`);
      }
    } catch (e: unknown) {
      logger.error(`[sleep] Sleep checker error:`, e);
    }
  }, sleepCheckIntervalMs);

  logger.log(
    `[platform] Sleep checker registered (every 5min, max ${config.maxSleepPerCycle}/cycle, timeout ${config.sleepTimeoutMinutes}min)`,
  );
}
